import type { StoredConsent } from '../models'
import { useAnalyticsStore } from '../store'

import { LocalStorageProvider } from '#Common/support'
import { cookieConfirmKey, CookieStatus, consentStorageKey } from '~/app/composables/cookie/key'

/**
 * Согласие на cookies по категориям (ТЗ аналитики §11.2): чтение/запись решения
 * с TTL из cmp-конфига витрины. «Необходимые» cookies согласия не требуют
 */
export const useConsent = () => {
  const store = useAnalyticsStore()
  const browserStore = new LocalStorageProvider()

  function ttlDays(): number {
    return store.config?.cmp?.consentTtlDays ?? 365
  }

  function isExpired(consent: StoredConsent): boolean {
    const decidedAt = Date.parse(consent.decidedAt)
    if (Number.isNaN(decidedAt)) return true
    return Date.now() - decidedAt > ttlDays() * 24 * 60 * 60 * 1000
  }

  /**
   * Восстановить решение из localStorage (с миграцией старого одиночного ключа);
   * протухшее по TTL решение отбрасывается — баннер покажется снова
   */
  function restore(): StoredConsent | null {
    const stored = browserStore.get(consentStorageKey) as StoredConsent | null
    if (stored && !isExpired(stored)) {
      store.setConsent(stored)
      return stored
    }

    // Миграция прежнего бинарного согласия («Хорошо» = согласие на всё)
    const legacy = browserStore.get(cookieConfirmKey)
    if (legacy === CookieStatus.accept) {
      return decide({ analytics: true, marketing: true })
    }

    store.setConsent(null)
    return null
  }

  function decide(categories: { analytics: boolean, marketing: boolean }): StoredConsent {
    const consent: StoredConsent = {
      analytics: categories.analytics,
      marketing: categories.marketing,
      decidedAt: new Date().toISOString(),
    }
    browserStore.set(consentStorageKey, consent)
    store.setConsent(consent)
    return consent
  }

  const acceptAll = () => decide({ analytics: true, marketing: true })
  const acceptNecessaryOnly = () => decide({ analytics: false, marketing: false })

  /**
   * Разрешён ли сбор аналитики: согласие не требуется (CMP выключен или категория
   * не требует согласия) либо пользователь дал согласие на «аналитика»
   */
  function analyticsAllowed(): boolean {
    const cmp = store.config?.cmp
    if (!cmp?.enabled || !cmp.analyticsCookiesRequireConsent) return true
    return store.consent?.analytics === true
  }

  /** Требуется ли показать баннер (CMP включён и решения нет/протухло) */
  function bannerRequired(): boolean {
    return Boolean(store.config?.cmp?.enabled) && store.consent === null
  }

  return {
    restore,
    acceptAll,
    acceptNecessaryOnly,
    analyticsAllowed,
    bannerRequired,
  }
}
